import {
  OWNER_JARVIS_CONFIRMATION,
  OWNER_JARVIS_OPERATION_KIND,
  defineOwnerJarvisCapability,
} from "../ownerJarvisCapabilityContract";
import { includesAny, spokenDuration, spokenMoney } from "../ownerJarvisUtils";
import { explainOwnerJarvisReasons } from "../ownerJarvisReasonCodes";

export function createOwnerJarvisOverviewCapability({ snapshotService }) {
  return {
    id: "owner-overview",
    async handle({ text }) {
      const asksWaiting = includesAny(text, ["que estoy esperando", "que estamos esperando", "a quien estoy esperando", "seguimientos vencidos", "esperas vencidas", "que esperas tengo"]);
      if (!includesAny(text, ["hola jarvis", "jarvis estas ahi", "resumen del dia", "resumen de hoy", "que tengo hoy", "como viene mi dia", "que hago ahora", "por donde empiezo", "como esta mi plata", "cuanto trabaje hoy", "que sabes de mi"]) && !asksWaiting) return null;

      const requestedScopes = asksWaiting
        ? ["waiting"]
        : includesAny(text, ["como esta mi plata"])
        ? ["finance"]
        : includesAny(text, ["cuanto trabaje hoy"])
          ? ["today"]
          : ["today", "projects", "finance", "commitments", "routines"];
      const snapshot = await snapshotService.current({ scopes: requestedScopes });
      const today = snapshot.today || {};
      const money = snapshot.finance || {};
      const projects = snapshot.projects || {};
      const waiting = snapshot.waiting || { count: 0, overdueCount: 0, items: [] };
      const direction = today.direction || { kind: "free", highlightSection: "today-jarvis" };
      const directionWhy = explainOwnerJarvisReasons(direction.reasons);
      const currency = money.currency || snapshot.context?.currency || "ARS";
      const workMinutes = Number(today.workMinutes || 0);
      const available = Number(money.available || 0);
      const outstanding = Number(money.outstanding || 0);
      const openCommitmentCount = Number(today.openCommitmentCount || 0);
      const pendingRoutineCount = Number(today.pendingRoutineCount || 0);

      if (asksWaiting) {
        const items = waiting.items || [];
        const asksDue = includesAny(text, ["seguimientos vencidos", "esperas vencidas"]);
        const selected = asksDue ? items.filter((item) => item.followUpDue) : items;
        if (!selected.length) return { handled: true, highlightSection: "today-jarvis", message: asksDue ? "No veo seguimientos de espera vencidos." : "No veo esperas a terceros abiertas. No voy a inventar ninguna." };
        const visible = selected.slice(0, 6).map((item) => {
          const person = item?.person?.name ? ` de ${item.person.name}` : "";
          const what = item.waitingFor || item.label || "algo pendiente";
          const review = item.followUpDue ? "; ya toca revisarlo" : item.reviewAt ? `; revisar ${new Date(item.reviewAt).toLocaleDateString("es-AR")}` : "";
          const since = Number.isFinite(Number(item.waitingDays)) ? `, hace ${Number(item.waitingDays)} día(s)` : "";
          return `${what}${person}${since}${review}`;
        });
        const more = selected.length > visible.length ? ` Hay ${selected.length - visible.length} más.` : "";
        return { handled: true, highlightSection: "today-jarvis", message: `${asksDue ? "Seguimientos de espera vencidos" : "Ahora estás esperando"}: ${visible.join("; ")}.${more}` };
      }

      if (includesAny(text, ["cuanto trabaje hoy"])) return { handled: true, highlightSection: "today-agenda", message: `Hoy llevás ${spokenDuration(workMinutes)} de trabajo real registrado.` };
      if (includesAny(text, ["como esta mi plata"])) return { handled: true, highlightSection: "today-money", message: `Te marco el dinero. Tenés ${spokenMoney(available, currency)} disponible.${outstanding > 0 ? ` Hay ${spokenMoney(outstanding, currency)} por cobrar.` : ""}` };
      if (includesAny(text, ["que hago ahora", "por donde empiezo"])) {
        if (direction.kind === "project_review" && direction.label) return { handled: true, highlightSection: direction.highlightSection, message: [direction.label, directionWhy, "Empecemos por ahí."].filter(Boolean).join(" ") };
        if (direction.kind === "commitment" && direction.label) return { handled: true, highlightSection: direction.highlightSection, message: ["Tus proyectos están ordenados.", `Ahora sigue ${direction.label}.`, directionWhy].filter(Boolean).join(" ") };
        if (direction.kind === "receivables") return { handled: true, highlightSection: direction.highlightSection, message: ["No hay una obligación inmediata. Te marco lo que está por cobrar.", directionWhy].filter(Boolean).join(" ") };
        if (direction.kind === "routine" && direction.label) return { handled: true, highlightSection: direction.highlightSection, message: [`Lo próximo que ya existe es ${direction.label}.`, directionWhy].filter(Boolean).join(" ") };
        return { handled: true, message: "No hay una obligación inmediata. El bloque puede quedar libre; no hace falta crear una tarea para ocuparlo." };
      }
      if (includesAny(text, ["que sabes de mi"])) {
        return { handled: true, message: `Estoy conectado a tu agenda, movimientos, clientes, proyectos, rutinas, trabajo real y recuerdos de Owner 360. Hoy veo ${openCommitmentCount} tareas abiertas, ${pendingRoutineCount} rutinas pendientes y ${spokenDuration(workMinutes)} registrados.` };
      }
      const projectReviews = Number(projects.pendingDailyReviewCount || 0);
      const summary = projectReviews > 0 ? "Hay proyectos que necesitan una decisión. Te marco el primero." : openCommitmentCount || pendingRoutineCount ? "Los proyectos están ordenados. Te marco lo que sigue hoy." : "Todo está bastante despejado. No hace falta inventar trabajo.";
      return { handled: true, highlightSection: today.overviewHighlightSection || "today-money", message: [summary, directionWhy].filter(Boolean).join(" ") };
    },
  };
}

export function createOwnerJarvisInventoryCapability({ ownerApi }) {
  return defineOwnerJarvisCapability({
    id: "inventory-query",
    domain: "inventory",
    description: "Consulta el inventario personal existente sin inferir faltantes ni necesidades.",
    reads: ["owner.inventory"],
    identify({ text }) {
      if (!includesAny(text, ["que tengo en stock", "stock personal", "que comida tengo", "que alimentos tengo"])) return null;
      return { operationId: "list" };
    },
    operations: [{
      id: "list",
      kind: OWNER_JARVIS_OPERATION_KIND.READ,
      confirmation: OWNER_JARVIS_CONFIRMATION.NEVER,
      async execute() {
        const response = await ownerApi.listInventoryItems({ limit: 20 });
        const items = response?.items || [];
        if (!items.length) return { handled: true, message: "Todavía no tenés stock personal cargado." };
        const summary = items.slice(0, 8).map((item) => `${item.name}: ${Number(item.quantity || 0).toLocaleString("es-AR")} ${item.unit || "unidades"}`).join(", ");
        return { handled: true, message: `En stock tengo registrado: ${summary}.` };
      },
      errors: { execute: "No pude consultar tu stock personal ahora. Probá de nuevo." },
    }],
    errors: { unsupported: "Esa operación de inventario todavía no está habilitada." },
  });
}
